import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';

interface FormFieldProps {
  label: string;
  htmlFor: string;
  error?: string;
  hint?: string;
  /** Shows a subtle red asterisk after the label so users know the field is mandatory before they submit. */
  required?: boolean;
  children: ReactNode;
}

export function FormField({ label, htmlFor, error, hint, required, children }: FormFieldProps) {
  return (
    <div className="space-y-1">
      <label htmlFor={htmlFor} className="block text-sm font-medium text-scope-navy">
        {label}
        {required && (
          <span aria-hidden="true" className="ml-0.5 text-status-red">
            *
          </span>
        )}
        {required && <span className="sr-only"> (required)</span>}
      </label>
      {children}
      {hint && !error && <p className="text-xs text-scope-text-muted">{hint}</p>}
      {error && <p className="text-xs text-status-red">{error}</p>}
    </div>
  );
}

const inputClasses =
  'w-full rounded-lg border border-scope-border bg-white px-3 py-2 text-sm text-scope-navy placeholder:text-scope-text-faint focus:outline-none focus:ring-2 focus:ring-scope-green focus:border-transparent disabled:bg-scope-cream-warm disabled:opacity-60';

export const TextInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function TextInput({ className = '', ...rest }, ref) {
    return <input ref={ref} className={`${inputClasses} ${className}`} {...rest} />;
  },
);

export const TextArea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function TextArea({ className = '', rows = 4, ...rest }, ref) {
  return <textarea ref={ref} rows={rows} className={`${inputClasses} ${className}`} {...rest} />;
});
